/**
 * Withdrawal Hook
 * 출금 페이지 데이터 관리 및 API 호출 로직
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { getWithdrawalAccount, requestWithdrawal } from '../api/withdrawalApi';
import { 
    WithdrawalAccountResponse,
    WithdrawalRequestRequest,
    WithdrawalRequestResponse,
} from '../types/withdrawal';

/**
 * 출금 페이지 Hook
 */
export const useWithdrawal = () => {
    // 상태 관리
    const [accountInfo, setAccountInfo] = useState<WithdrawalAccountResponse | null>(null);
    
    // API 호출 상태 관리
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [isRequesting, setIsRequesting] = useState<boolean>(false);
    const [requestError, setRequestError] = useState<Error | null>(null);

    // 중복 요청 방지를 위한 ref (상태 업데이트 전에도 즉시 체크)
    const isRequestingRef = useRef<boolean>(false);
    const lastRequestTimeRef = useRef<number>(0);
    // 마지막 에러 메시지 ref (async 클로저 stale 문제 우회)
    const lastErrorMessageRef = useRef<string | null>(null);

    /**
     * 출금 계좌 정보 조회
     */
    const fetchWithdrawalAccount = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const responseData = await getWithdrawalAccount();

            if (responseData.success) {
                setAccountInfo(responseData);
            } else {
                setError(new Error(responseData.message || '계좌 정보 조회에 실패했습니다.'));
            }
        } catch (error) {
            setError(error as Error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * 출금 신청 (중복 방지 강화)
     */
    const handleWithdrawalRequest = useCallback(async (
        reqVal: number,
        briefs = ''
    ): Promise<WithdrawalRequestResponse | null> => {
        // 1. 이미 요청 중이면 무시
        if (isRequestingRef.current) {
            const msg = '출금 요청이 이미 진행 중입니다. 잠시만 기다려주세요.';
            lastErrorMessageRef.current = msg;
            setRequestError(new Error(msg));
            return null;
        }

        // 2. 마지막 요청으로부터 2초 이내 재요청 방지 (디바운스)
        const now = Date.now();
        const timeSinceLastRequest = now - lastRequestTimeRef.current;
        if (timeSinceLastRequest < 2000) {
            const msg = '너무 빠른 연속 요청입니다. 잠시 후 다시 시도해주세요.';
            lastErrorMessageRef.current = msg;
            setRequestError(new Error(msg));
            return null;
        }

        // 3. 요청 시작
        isRequestingRef.current = true;
        lastRequestTimeRef.current = now;
        setIsRequesting(true);
        setRequestError(null);

        try {
            const requestData: WithdrawalRequestRequest = {
                reqVal,
                briefs,
            };

            const responseData = await requestWithdrawal(requestData);

            if (responseData.success) {
                lastErrorMessageRef.current = null;
                return responseData;
            } else {
                const errorMessage = responseData.message || '출금 신청에 실패했습니다.';
                lastErrorMessageRef.current = errorMessage;
                setRequestError(new Error(errorMessage));
                return null;
            }
        } catch (error) {
            const errorMessage = (error as Error).message || '출금 신청에 실패했습니다.';
            lastErrorMessageRef.current = errorMessage;
            setRequestError(error as Error);
            return null;
        } finally {
            // 4. 요청 완료
            isRequestingRef.current = false;
            setIsRequesting(false);
        }
    }, []);

    // 컴포넌트 마운트 시 계좌 정보 조회
    useEffect(() => {
        fetchWithdrawalAccount();
    }, [fetchWithdrawalAccount]);

    return {
        // 상태
        accountInfo,

        // 로딩 상태
        isLoading,
        error,

        // 출금 신청 상태
        isRequesting,
        requestError,
        lastErrorMessageRef,

        // 핸들러
        fetchWithdrawalAccount,
        handleWithdrawalRequest,
    };
};

